import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppliedChange, CycleDetail, formatDate, LearningStatus, Plan, TargetDetail, TargetResults, TargetType } from '../../services/learningApi';
import { credits, CycleStatusChip, RunLink, StatCard, stepLabel, useApis } from './common';
import { pct, VerdictChip } from './ResultsTab';
import { CycleDetailDialog, CycleLog, CycleReportView } from './CycleViews';

interface Props {
  targetType: TargetType;
  targetId: string;
  detail: TargetDetail;
  status: LearningStatus;
  onTab: (tab: string) => void;
  onChanged: () => void;
}

const ActiveCycle: React.FC<{ cycleId: string; onFinished: () => void }> = ({ cycleId, onFinished }) => {
  const apis = useApis();
  const [cycle, setCycle] = useState<CycleDetail | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const c = await apis.learning.cycle(cycleId, 1, 0);
        if (!alive) return;
        setCycle(c);
        if (c.status !== 'queued' && c.status !== 'running') {
          onFinished();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (alive) timer = setTimeout(poll, 3000);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [apis, cycleId, onFinished]);

  const planned = cycle?.stats?.planned || cycle?.plan?.planned || 0;
  const analyzed = cycle?.stats?.analyzed || 0;
  const pct = planned ? Math.min(100, Math.round((analyzed / planned) * 100)) : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderColor: 'info.main' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Analysis in progress
        </Typography>
        {cycle && <CycleStatusChip status={cycle.status} />}
        <Box sx={{ flex: 1 }} />
        {cycle && (
          <Typography variant="body2" color="text.secondary">
            {analyzed} / {planned} runs · {credits(cycle.credits_used)}
          </Typography>
        )}
        <Button
          size="small"
          color="warning"
          disabled={cancelling}
          onClick={async () => {
            setCancelling(true);
            try {
              await apis.learning.cancelCycle(cycleId);
            } finally {
              setCancelling(false);
            }
          }}
        >
          {cancelling ? 'Stopping…' : 'Stop'}
        </Button>
      </Stack>
      <LinearProgress variant={planned ? 'determinate' : 'indeterminate'} value={pct} sx={{ mb: 1.5, height: 6, borderRadius: 3 }} />
      {cycle?.status === 'queued' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Waiting for a free analysis slot…
        </Typography>
      )}
      <CycleLog log={cycle?.log || []} maxHeight={200} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        You can leave this page — the analysis keeps running. Stopping keeps everything learned so far.
      </Typography>
    </Paper>
  );
};

export const OverviewTab: React.FC<Props> = ({ targetType, targetId, detail, onTab, onChanged }) => {
  const apis = useApis();
  const [latest, setLatest] = useState<CycleDetail | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ counts: Record<string, number>; recent: any[] } | null>(null);
  const [results, setResults] = useState<TargetResults | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [apiMode, setApiMode] = useState<'one' | 'batch'>('one');
  const sourceType = targetType === 'chain' ? 'chain_execution' : 'function_run';
  const [openCycle, setOpenCycle] = useState<string | null>(null);
  const [showApi, setShowApi] = useState(false);
  const config = detail.config;
  const lastCycleAt = config?.last_cycle_at;

  useEffect(() => {
    if (!config) return;
    let alive = true;
    apis.learning
      .cycles(targetType, targetId, 10, 0)
      .then(async (res) => {
        const finished = res.cycles.find((c) => c.status !== 'queued' && c.status !== 'running');
        if (alive) setLastError(finished && finished.status === 'failed' ? finished.error : null);
        const done = res.cycles.find((c) => c.status === 'completed');
        if (done && alive) setLatest(await apis.learning.cycle(done.id, 1, 0));
      })
      .catch(() => undefined);
    apis.learning
      .targetFeedback(targetType, targetId)
      .then((f) => alive && setFeedback(f))
      .catch(() => undefined);
    apis.learning
      .results(targetType, targetId, 30)
      .then((r) => alive && setResults(r))
      .catch(() => undefined);
    apis.learning
      .preview(targetType, targetId)
      .then((p) => alive && setPlan(p))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apis, targetType, targetId, !!config, lastCycleAt]);

  if (!config) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Let this {targetType} learn from its own runs
        </Typography>
        <Typography color="text.secondary" paragraph>
          When turned on, easyAI periodically samples recent runs you haven't analyzed yet, works out what went well and what didn't, and:
        </Typography>
        <Box component="ul" sx={{ mt: 0, color: 'text.secondary' }}>
          <li>builds a knowledge base of what works (for example, which substitutes customers accept for a product), used automatically on future runs;</li>
          <li>suggests improvements to instructions and settings for you to review — nothing changes without your approval;</li>
          <li>writes a short report of what works, what fails, and where the gaps are.</li>
        </Box>
        <Typography color="text.secondary" paragraph>
          It works without any feedback. If you can send thumbs up/down, clicks or purchases, results get better. Analysis is billed to your credits; check
          the estimate under Settings.
        </Typography>
        <Button variant="outlined" onClick={() => onTab('settings')}>
          Review settings
        </Button>
      </Paper>
    );
  }

  const nodeLabel = (id: string) => stepLabel(detail.target.nodes || [], id);
  const counts = config.entry_counts || {};
  const live = (counts.active || 0) + (counts.pinned || 0);
  const totalFeedback = feedback ? Object.values(feedback.counts).reduce((a, b) => a + b, 0) : 0;
  const comparisons = Object.fromEntries((results?.changes || []).map((c) => [c.id, c]));
  const worse = (results?.changes || []).filter((c) => c.verdict.direction === 'worse');

  return (
    <Box>
      {detail.active_cycle && <ActiveCycle cycleId={detail.active_cycle.id} onFinished={onChanged} />}
      {!detail.active_cycle && lastError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Last analysis failed: {lastError}
        </Alert>
      )}

      {(!!counts.candidate || !!config.open_suggestions || config.profile_status === 'none' || worse.length > 0) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            Needs your attention
          </Typography>
          <Stack spacing={1}>
            {worse.map((c) => (
              <AttentionRow
                key={c.id}
                text={`Results got worse after “${c.title}” (${
                  c.verdict.basis === 'ratings'
                    ? `positive ratings ${pct(c.before?.pct_positive)} → ${pct(c.after?.pct_positive)}`
                    : `success rate ${pct(c.before?.success_rate)} → ${pct(c.after?.success_rate)}`
                }). Check it, and consider switching back${targetType === 'function' ? ' to the previous version' : ' via Version history'}.`}
                action="See results"
                onClick={() => onTab('results')}
              />
            ))}
            {config.profile_status === 'none' && (
              <AttentionRow
                text="No learning profile yet. One is generated automatically on the first analysis, but reviewing it first (with a few hints about what “good” means) gives much better results."
                action="Set up profile"
                onClick={() => onTab('profile')}
              />
            )}
            {!!counts.candidate && (
              <AttentionRow
                text={`${counts.candidate} learning${counts.candidate === 1 ? ' is' : 's are'} waiting. They aren't used until more runs back them up or you approve them.`}
                action="Review learnings"
                onClick={() => onTab('knowledge')}
              />
            )}
            {!!config.open_suggestions && (
              <AttentionRow
                text={`${config.open_suggestions} suggested change${config.open_suggestions === 1 ? '' : 's'} to review.`}
                action="Review suggestions"
                onClick={() => onTab('suggestions')}
              />
            )}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Knowledge in use"
            value={live}
            hint={`${counts.candidate || 0} waiting${counts.rejected ? ` · ${counts.rejected} rejected` : ''}`}
            action={
              <Button size="small" onClick={() => onTab('knowledge')}>
                Browse
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Open suggestions"
            value={config.open_suggestions}
            action={
              <Button size="small" onClick={() => onTab('suggestions')}>
                Review
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Last analysis"
            value={config.last_cycle_at ? <CycleStatusChip status={config.last_cycle_status} /> : 'Never'}
            hint={
              <>
                {config.last_cycle_at ? formatDate(config.last_cycle_at) : `Schedule: ${detail.settings.schedule.mode}`}
                {plan && (
                  <Tooltip
                    title={`Runs from the last ${detail.settings.sampling.window_days} days (Settings → Look back) that no analysis has read yet. This is what the next analysis works from, and it can include runs from before your last change. A manual analysis needs at least 5; scheduled ones wait for your minimum of ${detail.settings.sampling.min_runs}.`}
                  >
                    <Box component="span" sx={{ display: 'block', cursor: 'help' }}>
                      <b>{plan.eligible}</b> run{plan.eligible === 1 ? '' : 's'} waiting to be analyzed
                    </Box>
                  </Tooltip>
                )}
              </>
            }
            action={
              <Button size="small" onClick={() => onTab('history')}>
                History
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Knowledge base"
            value={detail.knowledge ? `${detail.knowledge.chunks} snippets` : '—'}
            hint={
              detail.knowledge ? (
                <>
                  <Box component="span" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {detail.knowledge.category_name}
                  </Box>
                  {!config.enabled
                    ? 'Not used while self-improvement is off'
                    : !detail.settings.knowledge.inject
                      ? 'Not used (automatic use is off in Settings)'
                      : detail.knowledge.chunks
                        ? 'Used automatically'
                        : 'Nothing in use yet'}
                </>
              ) : (
                'Created after the first analysis'
              )
            }
            action={
              <Button size="small" onClick={() => onTab('knowledge')}>
                Details
              </Button>
            }
          />
        </Grid>
      </Grid>

      {!!detail.recent_changes?.length && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Changes you applied
            </Typography>
            <Button size="small" onClick={() => onTab('results')}>
              Before / after
            </Button>
          </Stack>
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            {detail.recent_changes.map((c) => (
              <Stack key={c.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {c.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {describeChange(c, nodeLabel)} · {formatDate(c.decided_at)}
                    {c.decided_by ? ` by ${c.decided_by}` : ''}
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    c.runs_since === 0
                      ? 'No runs since this change. Its effect shows up once there are new runs and the next analysis looks at them.'
                      : 'Runs made after this change was applied. The next analysis reads every run it hasn’t seen yet (see “waiting to be analyzed” above), which can also include older ones.'
                  }
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    color={c.runs_since === 0 ? 'warning' : 'default'}
                    label={c.runs_since === 0 ? 'No runs since this change' : `${c.runs_since} run${c.runs_since === 1 ? '' : 's'} since this change`}
                  />
                </Tooltip>
                {comparisons[c.id] && results && (
                  <VerdictChip verdict={comparisons[c.id].verdict} minRatings={results.thresholds.min_ratings} minRuns={results.thresholds.min_runs} />
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Latest report
              </Typography>
              {latest && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(latest.completed_at || latest.started_at)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" onClick={() => setOpenCycle(latest.id)}>
                    Details
                  </Button>
                </>
              )}
            </Stack>
            {latest ? <CycleReportView report={latest.report} /> : <Typography color="text.secondary">Run an analysis to get the first report.</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Feedback received
            </Typography>
            {totalFeedback === 0 ? (
              <Typography variant="body2" color="text.secondary" paragraph>
                None yet — that's fine. Runs are judged by failures, reruns and an AI quality check. Feedback makes it sharper.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}>
                {Object.entries(feedback!.counts).map(([label, n]) => (
                  <Chip key={label} size="small" label={`${label}: ${n}`} variant="outlined" />
                ))}
              </Stack>
            )}
            {feedback?.recent.slice(0, 5).map((f) => (
              <Stack key={f.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip size="small" label={f.label} color={f.score > 0 ? 'success' : f.score < 0 ? 'error' : 'default'} />
                <RunLink type={f.source_type} id={f.source_id} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatDate(f.created_at)}
                </Typography>
              </Stack>
            ))}
            <Link component="button" variant="body2" onClick={() => setShowApi((v) => !v)} sx={{ mt: 1 }}>
              {showApi ? 'Hide' : 'How to send feedback'}
            </Link>
            {showApi && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Use the thumbs on any run's details page, or send it from your app with your API key:
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={apiMode} onChange={(_, v) => v && setApiMode(v)} sx={{ mb: 1 }}>
                  <ToggleButton value="one">One</ToggleButton>
                  <ToggleButton value="batch">Many (up to 100)</ToggleButton>
                </ToggleButtonGroup>
                <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 11.5, overflow: 'auto', m: 0 }}>
                  {apiMode === 'one'
                    ? `POST /api/v2/feedback
Authorization: Bearer <your API key>
{
  "source_type": "${sourceType}",
  "source_id": "<run or execution id>",
  "label": "thumbs_up",
  "item_ref": "<optional: which item>",
  "note": "<optional>",
  "metadata": { "feedback_id": "<optional: your id>" }
}`
                    : `POST /api/v2/feedback/batch
Authorization: Bearer <your API key>
{
  "items": [
    { "source_type": "${sourceType}",
      "source_id": "<id 1>", "label": "accepted",
      "metadata": { "feedback_id": "evt-101" } },
    { "source_type": "${sourceType}",
      "source_id": "<id 2>", "label": "edited",
      "item_ref": "sku-42",
      "note": "Should have been oat milk",
      "metadata": { "feedback_id": "evt-102" } }
  ]
}

→ { "created": [...], "rejected": [...],
    "duplicates": [...] }`}
                </Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  {apiMode === 'batch'
                    ? 'Items can be for different runs. Unknown runs come back in “rejected”; the rest are still saved. '
                    : ''}
                  Send the same <code>feedback_id</code> again and it’s counted once, so retries are safe.
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, mb: 0.5 }}>
                  Labels and how much each counts (any other label counts as neutral unless you send <code>score</code>, −1 to 1):
                </Typography>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {FEEDBACK_LABELS.map(([label, s, help]) => (
                    <Tooltip key={label} title={help}>
                      <Chip size="small" variant="outlined" color={s > 0 ? 'success' : 'error'} label={`${label} ${s > 0 ? '+' : ''}${s}`} />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {openCycle && <CycleDetailDialog cycleId={openCycle} onClose={() => setOpenCycle(null)} />}
    </Box>
  );
};

const FEEDBACK_LABELS: [string, number, string][] = [
  ['purchase', 1, 'They bought / converted on the result'],
  ['accepted', 0.8, 'They used the result as-is (also: logged)'],
  ['thumbs_up', 0.8, 'Explicit “good”'],
  ['click', 0.6, 'They opened an item. Weak: interest, not proof it was right'],
  ['saved', 0.6, 'They kept it for later'],
  ['edited', -0.3, 'They changed it before using it. With item_ref + note (what it should have been) this is the most useful signal you can send'],
  ['correction', -0.5, 'They told you the right answer. Put it in note'],
  ['thumbs_down', -0.8, 'Explicit “bad”'],
  ['rejected', -0.8, 'They removed or dismissed it (also: discarded)'],
  ['returned', -1, 'Bought, then returned'],
  ['reported', -1, 'Flagged as wrong, unsafe or offensive'],
];

function describeChange(c: AppliedChange, nodeLabel: (id: string) => string): string {
  const r = c.applied_result || {};
  if (c.manual) return 'Marked as done (applied by hand)';
  if (r.new_version) {
    const repointed = r.nodes_repointed ? ` · ${r.nodes_repointed} step${r.nodes_repointed === 1 ? '' : 's'} switched to it` : '';
    return `New function version v${r.new_version}${repointed}`;
  }
  if (r.node_id && r.field) {
    const value = typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value);
    return `${nodeLabel(r.node_id)}: ${r.field} → ${value.length > 40 ? `${value.slice(0, 40)}…` : value}`;
  }
  return 'Applied';
}

const AttentionRow: React.FC<{ text: string; action: string; onClick: () => void }> = ({ text, action, onClick }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
    <Typography variant="body2" sx={{ flex: 1 }}>
      {text}
    </Typography>
    <Button size="small" variant="outlined" onClick={onClick} sx={{ flexShrink: 0 }}>
      {action}
    </Button>
  </Stack>
);
